import useLanguageContext from "@/hooks/useLanguageContext";
import { useTranslation } from "react-i18next";

type Feature = {
    title: string;
    description: string;
};

const accessibilityFeaturesEn: Feature[] = [
    {
        title: "Skip to Main Content",
        description:
            "Quickly jump to the main content of each page without navigating through repeated menus using keyboard shortcuts.",
    },
    {
        title: "Icons with Text Labels",
        description:
            "Important actions and navigation items use icons paired with clear text labels to support users with learning disabilities.",
    },
    {
        title: "Keyboard-Friendly Navigation",
        description:
            "All primary navigation, dropdown menus, and interactive elements can be accessed and used via keyboard.",
    },
    {
        title: "Dropdown & Expand/Collapse Menus",
        description:
            "Accessible dropdowns and expandable lists allow screen reader and keyboard users to navigate nested options easily.",
    },
    {
        title: "Accessibility Options Panel",
        description:
            "A dedicated controls panel allows users to adjust text size, spacing, contrast, and other reading-related enhancements.",
    },
    {
        title: "Text Size and Readability Tools",
        description:
            "Users can increase or decrease text size, reset to default size, increase spacing between elements, and adjust line height for improved readability.",
    },
    {
        title: "Color Contrast Adjustments",
        description:
            "Multiple contrast modes support visual accessibility including highlighting links, grayscale, invert colors, high saturation, and default saturation.",
    },
    {
        title: "Visibility and Display Controls",
        description:
            "Additional controls include enabling a screen reader assistance mode, activating a large cursor for visibility, and hiding images for distraction-free reading.",
    },
    {
        title: "Table Headers & Structure",
        description:
            "Table headers are programmatically associated with their cells, improving screen reader interpretation of tabular data.",
    },
    {
        title: "Headings & Page Structure",
        description:
            "Pages use clear heading levels H1 indicates the main heading, whereas H2 indicates a subheading to create a logical structure, helping assistive technologies navigate efficiently.",
    },
    {
        title: "Explicit Label Association",
        description:
            "Form controls (text boxes, radio buttons, checkboxes, dropdowns) are linked to visible labels for better accessibility.",
    },
];

const accessibilityFeaturesMr: Feature[] = [
    {
        title: "मुख्य सामग्रीवर जा",
        description:
            "कीबोर्ड शॉर्टकट वापरून वारंवार मेनूमधून न जाता प्रत्येक पेजच्या मुख्य मजकुरावर त्वरित जा.",
    },
    {
        title: "मजकूर लेबल्स असलेले चिन्ह",
        description:
            "महत्त्वाच्या कृती आणि नेव्हिगेशन आयटममध्ये शिकण्याची अक्षमता असलेल्या वापरकर्त्यांना समर्थन देण्यासाठी स्पष्ट मजकूर लेबलसह जोडलेले आयकॉन वापरले जातात.",
    },
    {
        title: "कीबोर्ड-अनुकूल नेव्हिगेशन",
        description:
            "सर्व प्राथमिक नेव्हिगेशन, ड्रॉपडाउन मेनू आणि परस्परसंवादी घटक कीबोर्डद्वारे ॲक्सेस आणि वापरले जाऊ शकतात.",
    },
    {
        title: "ड्रॉपडाउन आणि एक्सपांड/कोलॅप्स मेनू",
        description:
            "प्रवेशयोग्य ड्रॉपडाउन आणि विस्तारयोग्य सूची स्क्रीन रीडर आणि कीबोर्ड वापरकर्त्यांना नेस्टेड पर्याय सहजपणे नेव्हिगेट करण्यास अनुमती देतात.",
    },
    {
        title: "प्रवेशयोग्यता पर्याय पॅनेल",
        description:
            "एक समर्पित नियंत्रण पॅनेल वापरकर्त्यांना मजकूर आकार, अंतर, कॉन्ट्रास्ट आणि इतर वाचन-संबंधित सुधारणा समायोजित करण्यास अनुमती देते.",
    },
    {
        title: "मजकुराचा आकार आणि वाचनीयता साधने",
        description:
            "वापरकर्ते मजकूर आकार वाढवू किंवा कमी करू शकतात, डीफॉल्ट आकारावर रीसेट करू शकतात, घटकांमधील अंतर वाढवू शकतात आणि सुधारित वाचनीयतेसाठी ओळीची उंची समायोजित करू शकतात.",
    },
    {
        title: "रंग कॉन्ट्रास्ट समायोजने",
        description:
            "अनेक कॉन्ट्रास्ट मोड व्हिज्युअल सुलभतेला समर्थन देतात, ज्यात लिंक्स हायलाइट करणे, ग्रेस्केल, रंग उलटे करणे, उच्च सॅचुरेशन आणि डिफॉल्ट सॅचुरेशन यांचा समावेश आहे.",
    },
    {
        title: "दृश्यमानता आणि प्रदर्शन नियंत्रणे",
        description:
            "अतिरिक्त नियंत्रणांमध्ये स्क्रीन रीडर असिस्टन्स मोड सक्षम करणे, दृश्यमानतेसाठी मोठा कर्सर सक्रिय करणे आणि लक्ष विचलित न करता वाचनासाठी प्रतिमा लपवणे समाविष्ट आहे.",
    },
    {
        title: "टेबल हेडर आणि रचना",
        description:
            "टेबल हेडर प्रोग्रामॅटिकली त्यांच्या सेलशी जोडलेले असतात, ज्यामुळे टॅब्युलर डेटाचे स्क्रीन रीडर इंटरप्रिटेशन सुधारते.",
    },
    {
        title: "शीर्षके आणि पृष्ठ रचना",
        description:
            "पृष्ठे स्पष्ट शीर्षलेख पातळी वापरतात H1 मुख्य शीर्षलेख दर्शविते, तर H2 उपशीर्षक दर्शविते जेणेकरून तार्किक रचना तयार होईल, ज्यामुळे सहाय्यक तंत्रज्ञान कार्यक्षमतेने नेव्हिगेट करण्यास मदत होईल.",
    },
    {
        title: "स्पष्ट लेबल असोसिएशन",
        description:
            "चांगल्या सुलभतेसाठी फॉर्म नियंत्रणे (मजकूर बॉक्स, रेडिओ बटणे, चेकबॉक्सेस, ड्रॉपडाउन) दृश्यमान लेबलांशी जोडली जातात.",
    },
];

export const AccessibilityStatement = () => {

    const { t: _ } = useTranslation()
    const { lang } = useLanguageContext();

    const data = lang == "en" ? accessibilityFeaturesEn : accessibilityFeaturesMr

    return (
        <main className="px-4 py-8 md:px-8 lg:px-16">
            <section className="mx-auto flex flex-col gap-8">
                <header className="space-y-4">
                    <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 md:text-3xl">
                        {_("footer.accessibility_statement.title")}
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-300 md:text-base">
                        {_("footer.accessibility_statement.description")}
                    </p>
                </header>

                <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            {_("footer.accessibility_statement.sub_description")}
                        </h3>

                        <div className="grid gap-4 md:grid-cols-2">
                            {data.map((feature) => (
                                <article
                                    key={feature.title}
                                    className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-100/70 transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:shadow-none"
                                >
                                    <div className="mb-3 flex items-start gap-3">
                                        <div className="mt-1 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-emerald-50 text-sm font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                            •
                                        </div>
                                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-50 md:text-base">
                                            {feature.title}
                                        </h4>
                                    </div>
                                    <p className="text-xs text-slate-600 dark:text-slate-300 md:text-sm">
                                        {feature.description}
                                    </p>
                                </article>
                            ))}
                        </div>
                    </div>

                    <aside className="space-y-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-100/70 dark:border-slate-700 dark:bg-slate-900">
                        <div className="space-y-2">
                            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                                {_("footer.accessibility_statement.side_panel.title1")}
                            </h4>
                            <ul className="space-y-2 text-xs text-slate-600 dark:text-slate-300 md:text-sm">
                                <li>
                                    <span className="font-medium">{_("footer.accessibility_statement.side_panel.label1")}:</span> {_("footer.accessibility_statement.side_panel.description1.part1")}{" "}
                                    <kbd className="rounded border border-slate-300 bg-slate-100 px-1 text-[0.7rem] dark:border-slate-600 dark:bg-slate-800">
                                        Tab
                                    </kbd>{" "}
                                    {_("footer.accessibility_statement.side_panel.description1.part2")}{" "}
                                    <kbd className="rounded border border-slate-300 bg-slate-100 px-1 text-[0.7rem] dark:border-slate-600 dark:bg-slate-800">
                                        Enter
                                    </kbd>{" "}
                                    {_("footer.accessibility_statement.side_panel.description1.part3")}.
                                </li>
                                <li>
                                    <span className="font-medium">{_("footer.accessibility_statement.side_panel.label2")}:</span> {_("footer.accessibility_statement.side_panel.description2")}
                                </li>
                                <li>
                                    <span className="font-medium">{_("footer.accessibility_statement.side_panel.label3")}:</span> {_("footer.accessibility_statement.side_panel.description3")}
                                </li>
                            </ul>
                        </div>

                        <div className="space-y-2 rounded-xl bg-slate-50 p-4 text-xs text-slate-700 dark:bg-slate-800/70 dark:text-slate-200 md:text-sm">
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                {_("footer.accessibility_statement.side_panel.title2")}?
                            </h4>
                            <p>
                                {_("footer.accessibility_statement.side_panel.description")}
                            </p>
                            <div className="mt-2 space-y-1 text-xs">
                                <p>
                                    Email:{" "}
                                    <a
                                        href="mailto:acs.revnue@maharashtra.gov.in"
                                        className="font-medium text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400"
                                    >
                                        acs[dot]revnue[at]maharashtra[dot]gov[dot]in
                                    </a>
                                </p>
                                <p>Phone:{" "}
                                    <a href="tel:022-22024588" className="font-medium text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400">022-22024588</a>
                                </p>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
};
